import { ArrowRightOutlined, CloseOutlined } from "@ant-design/icons"
import { Button, Flex, Tooltip, Typography, theme } from "antd"
import React, { useEffect, useLayoutEffect, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { RoutesPaths } from "../../../constants/routes.ts"
import {
  computeSpotlightRect,
  computeTooltipPlacement,
  type TourRect
} from "./geometry.ts"
import { measureElement } from "./measure-element.ts"
import { useProductTour } from "./ProductTourContext.tsx"

const { Text, Title } = Typography

const TOOLTIP_SIZE = { width: 380, height: 220 }
const SPOTLIGHT_PADDING = 8

/** Re-measures the live target on resize/scroll/content-size-change — a
 * panel's own real height often changes the moment its data finishes
 * loading (a loading skeleton is shorter than the real table), and a
 * viewport-only resize listener would leave the spotlight sized for the
 * skeleton. `baseRect` (the engine's just-measured rect) is returned
 * directly until a live re-measurement produces a fresher one — mirroring
 * it into its own state via an effect would render one frame behind
 * `baseRect` on every step transition, flipping `isCentered` from true to
 * false a beat after the card first mounts and swapping CenteredCard for
 * AnchoredCard out from under anything that had already found the first
 * one's text. */
const useLiveRect = (baseRect: TourRect | null, target: string | undefined) => {
  const [liveOverride, setLiveOverride] = useState<TourRect | null>(null)

  useEffect(() => {
    setLiveOverride(null)
  }, [baseRect])

  useLayoutEffect(() => {
    if (!target) return
    const el = document.querySelector(`[data-guide-target="${target}"]`)
    if (!el) return

    const remeasure = () => {
      setLiveOverride(measureElement(el))
    }

    const resizeObserver = new ResizeObserver(remeasure)
    resizeObserver.observe(el)
    window.addEventListener("resize", remeasure)
    window.addEventListener("scroll", remeasure, true)
    return () => {
      resizeObserver.disconnect()
      window.removeEventListener("resize", remeasure)
      window.removeEventListener("scroll", remeasure, true)
    }
  }, [target, baseRect])

  return liveOverride ?? baseRect
}

const getBorderRadius = (target: string | undefined): string => {
  if (!target) return "8px"
  const el = document.querySelector(`[data-guide-target="${target}"]`)
  if (!el) return "8px"
  const radius = window.getComputedStyle(el).borderRadius
  return radius && radius !== "0px" ? radius : "8px"
}

const CenteredCard = ({
  contextLabel,
  title,
  body,
  ctaLabel,
  ctaLoading,
  skipLabel,
  backLabel,
  onAction,
  onBack,
  onSkip,
  onClose,
  showBack,
  stepCounter,
  secondaryCtaLabel,
  onSecondaryAction
}: {
  contextLabel?: string
  title: string
  body: string
  ctaLabel: string
  ctaLoading?: boolean
  skipLabel: string
  backLabel: string
  onAction: () => void
  onBack: () => void
  onSkip: () => void
  onClose: () => void
  showBack: boolean
  stepCounter: string
  /** The tour's own closing screen offers a second, optional action
   * ("Create your first Lead") alongside the primary one — every other
   * step keeps a single CTA. */
  secondaryCtaLabel?: string
  onSecondaryAction?: () => void
}) => {
  const { token } = theme.useToken()
  return (
    <div className="relative w-[440px] max-w-[92vw] rounded-xl bg-surface p-8 text-center shadow-2xl border border-border">
      <Tooltip title={skipLabel}>
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClose}
          aria-label={skipLabel}
          className="!absolute !right-2 !top-2"
        />
      </Tooltip>
      <Flex vertical align="center" gap={6}>
        {contextLabel && (
          <Text
            className="text-xs font-semibold tracking-widest uppercase"
            style={{ color: token.colorPrimary }}
          >
            {contextLabel}
          </Text>
        )}
        <Text type="secondary" className="text-xs">
          {stepCounter}
        </Text>
        <Title level={3} className="!mb-0">
          {title}
        </Title>
        <Text type="secondary" className="text-base">
          {body}
        </Text>
        <Flex
          align="center"
          gap={8}
          wrap="wrap"
          justify="center"
          className="mt-4"
        >
          {!secondaryCtaLabel && (
            <Button type="text" size="small" onClick={onSkip}>
              {skipLabel}
            </Button>
          )}
          {showBack && (
            <Button size="small" onClick={onBack}>
              {backLabel}
            </Button>
          )}
          {secondaryCtaLabel && onSecondaryAction && (
            <Button size="large" onClick={onSecondaryAction}>
              {secondaryCtaLabel}
            </Button>
          )}
          <Button
            type="primary"
            size="large"
            onClick={onAction}
            loading={ctaLoading}
            icon={ctaLoading ? undefined : <ArrowRightOutlined />}
            iconPosition="end"
          >
            {ctaLabel}
          </Button>
        </Flex>
      </Flex>
    </div>
  )
}

const AnchoredCard = ({
  contextLabel,
  title,
  body,
  ctaLabel,
  ctaLoading,
  skipLabel,
  backLabel,
  onAction,
  onBack,
  onSkip,
  onClose,
  showBack,
  stepCounter,
  style
}: {
  contextLabel?: string
  title: string
  body: string
  ctaLabel: string
  ctaLoading?: boolean
  skipLabel: string
  backLabel: string
  onAction: () => void
  onBack: () => void
  onSkip: () => void
  onClose: () => void
  showBack: boolean
  stepCounter: string
  style: React.CSSProperties
}) => {
  const { token } = theme.useToken()
  return (
    <div
      className="relative rounded-lg bg-surface p-5 shadow-2xl border border-border"
      style={{ width: TOOLTIP_SIZE.width, ...style }}
    >
      <Tooltip title={skipLabel}>
        <Button
          type="text"
          size="small"
          icon={<CloseOutlined />}
          onClick={onClose}
          aria-label={skipLabel}
          className="!absolute !right-1.5 !top-1.5"
        />
      </Tooltip>
      <Flex vertical gap={6} className="pr-5">
        {contextLabel && (
          <Text
            className="text-[11px] font-semibold tracking-widest uppercase"
            style={{ color: token.colorPrimary }}
          >
            {contextLabel}
          </Text>
        )}
        <Text type="secondary" className="text-xs">
          {stepCounter}
        </Text>
        <Title level={5} className="!mb-0">
          {title}
        </Title>
        <Text className="text-sm">{body}</Text>
        <Flex justify="space-between" align="center" className="mt-2">
          <Button type="text" size="small" onClick={onSkip}>
            {skipLabel}
          </Button>
          <Flex gap={8}>
            {showBack && (
              <Button size="small" onClick={onBack}>
                {backLabel}
              </Button>
            )}
            <Button
              type="primary"
              size="small"
              onClick={onAction}
              loading={ctaLoading}
            >
              {ctaLabel}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </div>
  )
}

export const ProductTourOverlay = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    isActive,
    status,
    activeStep,
    stepIndex,
    totalSteps,
    targetRect,
    next,
    back,
    skip
  } = useProductTour()
  const liveRect = useLiveRect(targetRect, activeStep?.target)

  // Escape always offers a way out, from any step, at any status — the one
  // fallback that needs no target, no route and no async chain to work.
  useEffect(() => {
    if (!isActive) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") skip()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [isActive, skip])

  if (!isActive || !activeStep) return null

  const isComplete = activeStep.type === "complete"
  const isCentered = !liveRect || activeStep.type === "intro" || isComplete
  const stepCounter = t("guide.tour.common.stepCounter", {
    current: stepIndex + 1,
    total: totalSteps
  })

  // A fallback with no step-authored copy of its own is either this step's
  // real target failing to appear (a timeout in `waitForTarget`) or an
  // outright error in the engine's async chain (network failure, a bad
  // selector, …) — `ProductTourContext.goToStep`'s `catch` resolves to the
  // exact same status for both. Either way, never silently reuse the
  // step's normal "found it" copy centered with nothing highlighted; say
  // plainly that this one couldn't be shown, and let the user move on.
  // Excludes "complete": it declares no `resolveRoute` at all, so it always
  // resolves through the same null-route branch as a genuine missing
  // record — intentional, not an error, and already has its own real copy.
  const missingTarget =
    status === "fallback" && !activeStep.fallbackTitleKey && !isComplete
  const title = missingTarget
    ? t("guide.tour.common.targetMissingTitle")
    : t(
        status === "fallback" && activeStep.fallbackTitleKey
          ? activeStep.fallbackTitleKey
          : activeStep.titleKey
      )
  const body = missingTarget
    ? t("guide.tour.common.targetMissingBody")
    : t(
        status === "fallback" && activeStep.fallbackBodyKey
          ? activeStep.fallbackBodyKey
          : activeStep.bodyKey
      )
  const contextLabel = activeStep.contextKey
    ? t(activeStep.contextKey)
    : t("guide.tour.common.title")
  const ctaLabel = missingTarget
    ? t("guide.tour.common.skipStep")
    : t(activeStep.ctaKey)
  const skipLabel = t("guide.tour.common.skip")
  const backLabel = t("guide.tour.common.back")

  const spotlight = liveRect
    ? computeSpotlightRect(liveRect, SPOTLIGHT_PADDING)
    : null
  const tooltipPosition = liveRect
    ? computeTooltipPlacement(
        liveRect,
        { width: window.innerWidth, height: window.innerHeight },
        activeStep.placement ?? "bottom",
        TOOLTIP_SIZE
      )
    : null

  return createPortal(
    <div className="fixed inset-0 z-[1200]" role="dialog" aria-label={title}>
      {spotlight ? (
        <div
          className="pointer-events-none fixed transition-all duration-150"
          style={{
            top: spotlight.top,
            left: spotlight.left,
            width: spotlight.width,
            height: spotlight.height,
            borderRadius: getBorderRadius(activeStep.target),
            // A neutral (never teal-tinted) dim + elevation — the crisp 2px
            // border alone is the "this is what we mean" cue; a colored glow
            // ring around it read as tinting the whole target area, fighting
            // "keep the actual UI visible."
            boxShadow:
              "0 0 0 9999px var(--dk-overlay, rgba(15, 23, 42, 0.5)), 0 6px 20px rgba(15, 23, 42, 0.25)",
            border: "2px solid var(--ant-color-primary, #2A7D8C)"
          }}
        />
      ) : (
        <div
          className="pointer-events-none fixed inset-0"
          style={{ background: "var(--dk-overlay, rgba(15, 23, 42, 0.5))" }}
        />
      )}

      {/* The card renders at every status, including "loading" — the
          previous behaviour (hiding it while loading) is exactly the "dimmed
          screen, no controls" bug: the spotlight/dim above always renders
          once the tour is active, so hiding the card left nothing on top of
          it. `ctaLoading` spins the primary button instead. */}
      {isCentered || !tooltipPosition ? (
        <div
          className="fixed"
          style={{
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)"
          }}
        >
          <CenteredCard
            contextLabel={contextLabel}
            title={title}
            body={body}
            ctaLabel={ctaLabel}
            ctaLoading={status === "loading"}
            skipLabel={skipLabel}
            backLabel={backLabel}
            onAction={
              missingTarget
                ? next
                : isComplete
                  ? () => {
                      navigate(RoutesPaths.home)
                      skip()
                    }
                  : next
            }
            onBack={back}
            onSkip={skip}
            onClose={skip}
            showBack={stepIndex > 0 && !isComplete}
            stepCounter={stepCounter}
            secondaryCtaLabel={
              isComplete
                ? t("guide.productTour.complete.createFirstLead")
                : undefined
            }
            onSecondaryAction={
              isComplete
                ? () => {
                    navigate(RoutesPaths.lead.add)
                    skip()
                  }
                : undefined
            }
          />
        </div>
      ) : (
        <div
          className="fixed"
          style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
        >
          <AnchoredCard
            contextLabel={contextLabel}
            title={title}
            body={body}
            ctaLabel={ctaLabel}
            ctaLoading={status === "loading"}
            skipLabel={skipLabel}
            backLabel={backLabel}
            onAction={next}
            onBack={back}
            onSkip={skip}
            onClose={skip}
            showBack={stepIndex > 0}
            stepCounter={stepCounter}
            style={{}}
          />
        </div>
      )}
    </div>,
    document.body
  )
}
